import React, {createContext, ReactNode, useCallback, useContext, useEffect, useState} from 'react';
import {App as AntdApp} from 'antd';
import {api} from '../services/api';

interface User {
    id: number;
    username: string;
    email?: string;
    createdAt?: string;
    roles?: string[];
    permissions?: string[];
    directPermissions?: string[];
    memberLevel?: number;
}

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    selectedApiKey: number | null;
    setSelectedApiKey: (keyId: number | null) => void;
    login: (username: string, password: string) => Promise<boolean>;
    register: (username: string, password: string, email: string, invitationCode: string) => Promise<boolean>;
    logout: () => void;
    refreshUser: () => Promise<void>;
    hasPermission: (code: string, strict?: boolean) => boolean;
    hasMemberLevel: (minLevel: number) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';
const API_KEY_KEY = 'selected_api_key';

const COOKIE_NAME = 'auth_token';
const TOKEN_MAX_AGE = 7200; // 2小时

const setAuthCookie = (token: string): void => {
    try {
        document.cookie = `${COOKIE_NAME}=${token}; path=/; SameSite=Lax; max-age=${TOKEN_MAX_AGE}`;
    } catch { /* ignore */
    }
};

const clearAuthCookie = (): void => {
    try {
        document.cookie = `${COOKIE_NAME}=; path=/; max-age=0`;
    } catch { /* ignore */
    }
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({children}) => {
    const {message: messageApi} = AntdApp.useApp();
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedApiKey, setSelectedApiKeyState] = useState<number | null>(null);

    // 初始化时从localStorage恢复selectedApiKey
    useEffect(() => {
        const storedApiKey = localStorage.getItem(API_KEY_KEY);
        if (storedApiKey) {
            setSelectedApiKeyState(parseInt(storedApiKey, 10));
        }
    }, []);
    useEffect(() => {
        const initAuth = () => {
            try {
                const token = localStorage.getItem(TOKEN_KEY);
                const storedUser = localStorage.getItem(USER_KEY);

                if (token && storedUser) {
                    const parsedUser = JSON.parse(storedUser);
                    setUser(parsedUser);
                    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                    setAuthCookie(token);
                    // 旧登录态无 memberLevel 字段：回拉 /users/me 补齐（存量回填专家等级），
                    // 失败静默保留旧数据，等级控制按 0 兜底
                    if (undefined === parsedUser.memberLevel) {
                        api.get('/api/v1/users/me')
                            .then(response => {
                                if (response.data && (response.data.code === '0' || response.data.code === 200)) {
                                    const data = response.data.data;
                                    if (data && data.userId) {
                                        const freshUser = {
                                            id: data.userId,
                                            username: data.username,
                                            roles: data.roles || [],
                                            permissions: data.permissions || [],
                                            memberLevel: data.memberLevel ?? 0
                                        };
                                        localStorage.setItem(USER_KEY, JSON.stringify(freshUser));
                                        setUser(freshUser);
                                    }
                                }
                            })
                            .catch(() => {
                                // 忽略：保持旧数据
                            });
                    }
                }
            } catch (error) {
                localStorage.removeItem(TOKEN_KEY);
                localStorage.removeItem(USER_KEY);
            } finally {
                setIsLoading(false);
            }
        };

        initAuth();
    }, []);

    const login = useCallback(async (username: string, password: string): Promise<boolean> => {
        try {
            setIsLoading(true);
            const response = await api.post('/api/v1/auth/login', {
                username,
                password,
            });

            if (response.data.code === "0" || response.data.code === 200) {
                const {
                    userId,
                    username: respUsername,
                    accessToken,
                    refreshToken,
                    roles,
                    permissions,
                    memberLevel
                } = response.data.data;
                const userData = {
                    id: userId,
                    username: respUsername || username,
                    roles: roles ? roles.split(',') : [],
                    permissions: permissions || [],
                    directPermissions: permissions || [],
                    memberLevel: memberLevel ?? 0
                };
                localStorage.setItem(TOKEN_KEY, accessToken);
                localStorage.setItem(USER_KEY, JSON.stringify(userData));
                setAuthCookie(accessToken);
                api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
                setUser(userData);
                messageApi.success('登录成功');
                return true;
            } else {
                messageApi.error(response.data.message || '登录失败');
                return false;
            }
        } catch (error: any) {
            const errorMsg = error.response?.data?.message || '登录失败，请检查用户名和密码';
            messageApi.error(errorMsg);
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [messageApi]);

    const register = useCallback(async (username: string, password: string, email: string, invitationCode: string): Promise<boolean> => {
        try {
            setIsLoading(true);
            const response = await api.post('/api/v1/auth/register', {
                username,
                password,
                email,
                invitationCode,
            });

            if (response.data.code === "0" || response.data.code === 200) {
                messageApi.success('注册成功，请登录');
                return true;
            } else {
                messageApi.error(response.data.message || '注册失败');
                return false;
            }
        } catch (error: any) {
            const errorMsg = error.response?.data?.message || '注册失败，请稍后重试';
            messageApi.error(errorMsg);
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [messageApi]);

    const logout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem(API_KEY_KEY);
        clearAuthCookie();
        delete api.defaults.headers.common['Authorization'];
        setUser(null);
        setSelectedApiKeyState(null);
        messageApi.success('已退出登录');
    }, [messageApi]);

    const refreshUser = useCallback(async (): Promise<void> => {
        try {
            const response = await api.get('/api/v1/users/me');
            if (response.data && (response.data.code === '0' || response.data.code === 200)) {
                const data = response.data.data;
                if (data && data.userId) {
                    const freshUser = {
                        id: data.userId,
                        username: data.username,
                        roles: data.roles || [],
                        permissions: data.permissions || [],
                        memberLevel: data.memberLevel ?? 0
                    };
                    localStorage.setItem(USER_KEY, JSON.stringify(freshUser));
                    setUser(freshUser);
                }
            }
        } catch {
            // ignore, keep existing data
        }
    }, []);

    const hasPermission = useCallback((code: string, strict?: boolean): boolean => {
        // 严格模式：只检查非ADMIN角色直接分配的权限（排除ADMIN角色自动获得的全量权限）
        if (strict) {
            return user?.directPermissions?.includes(code) ?? user?.permissions?.includes(code) ?? false;
        }
        // 非严格模式：ADMIN角色用户拥有所有权限的豁免权
        if (user?.roles?.includes('ADMIN')) {
            return true;
        }
        return user?.permissions?.includes(code) ?? false;
    }, [user]);

    const hasMemberLevel = useCallback((minLevel: number): boolean => {
        // ADMIN 豁免会员等级限制（与后端 hasMemberLevel 表达式一致）
        if (user?.roles?.includes('ADMIN')) {
            return true;
        }
        return (user?.memberLevel ?? 0) >= minLevel;
    }, [user]);

    const setSelectedApiKey = useCallback((keyId: number | null) => {
        setSelectedApiKeyState(keyId);
        if (keyId !== null) {
            localStorage.setItem(API_KEY_KEY, keyId.toString());
        } else {
            localStorage.removeItem(API_KEY_KEY);
        }
    }, []);

    const value: AuthContextType = {
        user,
        isAuthenticated: !!user,
        isLoading,
        selectedApiKey,
        setSelectedApiKey,
        login,
        register,
        logout,
        refreshUser,
        hasPermission,
        hasMemberLevel,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
